import React, { useState } from 'react';
import { Milestone } from '../../Projects/Milestones/index';
import MilestoneCard from '../../Projects/Milestones/MilestoneCard';
import MilestoneForm from '../../Projects/Milestones/MilestoneForm';
import { MilestoneFormData } from '../../Projects/Milestones/index';

interface InlineEditMilestoneProps {
  milestone: Milestone;
  onSave: (milestoneId: string, data: Partial<Milestone>) => Promise<void> | void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
}

const InlineEditMilestone: React.FC<InlineEditMilestoneProps> = ({
  milestone,
  onSave,
  onCancel,
  disabled = false,
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<MilestoneFormData>({
    _id: milestone._id || milestone.id,
    id: milestone.id,
    title: milestone.title,
    description: milestone.description || '',
    scheduledDate: milestone.scheduledDate instanceof Date
      ? milestone.scheduledDate.toISOString().split('T')[0]
      : typeof milestone.scheduledDate === 'string'
      ? milestone.scheduledDate.split('T')[0]
      : new Date().toISOString().split('T')[0],
    status: milestone.status,
    delayReason: milestone.delayReason || '',
    earlyCompletion: milestone.earlyCompletion || false,
    earlyCompletionReason: milestone.earlyCompletionReason || '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    if (disabled) return;
    setFormData({
      _id: milestone._id || milestone.id,
      id: milestone.id,
      title: milestone.title,
      description: milestone.description || '',
      scheduledDate: milestone.scheduledDate instanceof Date
        ? milestone.scheduledDate.toISOString().split('T')[0]
        : typeof milestone.scheduledDate === 'string'
        ? milestone.scheduledDate.split('T')[0]
        : new Date().toISOString().split('T')[0],
      status: milestone.status,
      delayReason: milestone.delayReason || '',
      earlyCompletion: milestone.earlyCompletion || false,
      earlyCompletionReason: milestone.earlyCompletionReason || '',
    });
    setIsEditing(true);
  };

  const handleFormDataChange = (data: Partial<MilestoneFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.scheduledDate) return;

    setIsSaving(true);
    try {
      const milestoneId = milestone._id || milestone.id || '';
      
      // Build update data, only including defined values and filtering out undefined
      const updateData: Partial<Milestone> = {};
      
      if (formData.title !== undefined) {
        updateData.title = formData.title;
      }
      if (formData.description !== undefined) {
        updateData.description = formData.description || undefined; // Convert empty string to undefined
      }
      if (formData.scheduledDate !== undefined) {
        updateData.scheduledDate = formData.scheduledDate;
      }
      if (formData.status !== undefined) {
        updateData.status = formData.status;
      }
      if (formData.delayReason !== undefined) {
        updateData.delayReason = formData.delayReason || undefined; // Convert empty string to undefined
      }
      if (formData.earlyCompletion !== undefined) {
        updateData.earlyCompletion = formData.earlyCompletion;
      }
      if (formData.earlyCompletionReason !== undefined) {
        updateData.earlyCompletionReason = formData.earlyCompletionReason || undefined; // Convert empty string to undefined
      }
      
      // Auto-set completedDate when status changes to completed
      if (formData.status === 'completed' && !milestone.completedDate) {
        updateData.completedDate = new Date().toISOString();
      }
      
      // Filter out undefined values before sending
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      ) as Partial<Milestone>;
      
      await onSave(milestoneId, cleanUpdateData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving milestone:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    onCancel?.();
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-wrapper ${className}`}>
        <div className="animate-slide-down">
          <MilestoneForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onSubmit={handleSave}
            onCancel={handleCancel}
            isLoading={isSaving}
            submitLabel="Save"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="relative">
        <MilestoneCard
          milestone={milestone}
          showActions={false}
        />
        {!disabled && (
          <button
            type="button"
            onClick={handleStartEdit}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-full p-1 shadow-md"
            aria-label="Edit milestone"
          >
            <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default InlineEditMilestone;

